<script>
import Layout from '@layouts/main'
import api from '@utils/api'
import Filters from '@views/filters-dialog'
import transmitterHeartbeats from '@src/models/transmitter-heartbeats'
import formatDate, { convertToUTC } from '@utils/format-date-with-time'
import TablePaginator from '@components/paginator'
import Parameters from './parameters'
import moment from 'moment'
import { mapState } from 'vuex'
import userHasRoles from '@utils/userPermissions'
import AdjustColumnsDialog from '@components/adjust-columns-dialog'
import { hideValues, updateColumns } from '@utils/helpers/columns'

export default {
  name: 'Heartbeats',
  components: {
    Layout,
    Filters,
    TablePaginator,
    Parameters,
    AdjustColumnsDialog,
  },
  data() {
    return {
      modelName: '',
      isLoading: false,
      allowedRolesToSeeDetails: [
        'SUPERUSER',
        'DICTIONARIES_OPERATOR',
        'DOCUMENTS_OPERATOR',
        'GUEST',
      ],
      headers: [
        { text: 'transmitter_id', value: 'transmitterId', sortable: true },
        {
          text: 'transmitter_version',
          value: 'firmwareVersion',
          sortable: true,
          show: true,
        },
        { text: 'transmitter_ip1', value: 'ip1', sortable: true, show: true },
        { text: 'transmitter_ip2', value: 'ip2', sortable: true, show: true },
        { text: 'date', value: 'receiveTime', sortable: true },
        {
          text: 'last_start',
          value: 'timeSinceLastStart',
          sortable: true,
          show: true,
        },
        {
          text: 'connected_plugs',
          value: 'connectedPlugs',
          sortable: true,
          show: true,
        },
        { text: 'history', value: 'history', sortable: false, show: true },
      ],
      rowsPerPage: [10, 25, 50],
      page: 1,
      totalPages: 0,
      sortBy: 'transmitterId',
      descending: true,
      first: false,
      last: false,
      content: [],
      pagination: {},
      totalElements: 0,
      showFilters: false,
      model: transmitterHeartbeats,
      showDialog: false,
      filters: null,
      transmitterId: null,
      dateRangeParams: {},
    }
  },
  computed: {
    params() {
      return {
        size: this.pagination.rowsPerPage || null,
        page: this.page || null,
        sort_property: this.sortBy || null,
        sort_direction: this.descending || null,
        ...this.filters,
        ...this.dateRangeParams,
      }
    },
    ...mapState({
      unitsMeasurementValue: state => state.userSettings.userSettings['unitsMeasurement'],
    }),
    unitsMeasurement() {
      return this.$store.getters['userSettings/getUnitsMeasurement']
    },
    getHeaders() {
      return !this.transmitterId
        ? this.headers
        : this.headers.slice(0, this.headers.length - 1)
    },
    userAllowedToNavigate() {
      return userHasRoles(
        this.$store.state.auth.userRole,
        this.allowedRolesToSeeDetails
      )
    },
  },
  watch: {
    pagination: {
      handler() {
        this.$store.commit('pagination/SET', {
          name: this.modelName,
          pagination: {
            page: this.page,
            rowsPerPage: this.pagination.rowsPerPage,
            sortBy: this.sortBy,
            descending: this.descending,
          },
        })
      },
      deep: true,
    },
  },
  created() {
    this.transmitterId = this.$route.params.id
    this.modelName = !this.transmitterId ? 'heartbeats' : 'heartbeatsHistory'
    this.getPaginator()
    this.getFilters()
  },

  methods: {
    setParams(value) {
      this.dateRangeParams = { ...value }
      this.getPaginator()
      if (value.clicked) {
        this.page = 1
        this.pagination.page = 1
      }
      this.getFilters()
      this.fetchData()
    },
    fetchData() {
      this.prepareParams()
      this.convertDateParamsToFilters()
      this.transmitterId = this.$route.params.id
      this.isLoading = true
      if (this.transmitterId) {
        api
          .getItemById('heartbeat', this.transmitterId, this.params)
          .then(response => {
            this.setResponseData(response)
          })
          .catch(() => {})
          .then(() => {
            this.isLoading = false
          })
      } else {
        api
          .getItems('heartbeat', this.params)
          .then(response => {
            this.setResponseData(response)
          })
          .catch(() => {
            this.content = []
            this.page = 1
            this.totalPages = 0
            this.pagination = {
              ...this.pagination,
              page: 0,
              rowsPerPage: 0,
              sortBy: this.sortBy,
              descending: this.descending,
            }
          })
          .then(() => {
            this.isLoading = false
          })
      }
    },
    setResponseData(response) {
      this.content = response.content
      this.totalPages = response.totalPages
      this.first = response.first
      this.last = response.last
      this.page = response.number + 1
      this.pagination = {
        ...this.pagination,
        page: 1,
        rowsPerPage: response.size,
        sortBy: this.sortBy,
        descending: this.descending,
      }
    },
    prepareParams() {
      if (!this.params.useStartDateTime) {
        let now = moment(new Date())
        this.params.start_date = convertToUTC(now.subtract(150, 'years'))
        delete this.params.start_time
      }
      if (!this.params.useEndDateTime) {
        this.params.end_date = convertToUTC(
          moment(new Date()).add(200, 'years')
        )
        delete this.params.end_time
      }
      if (
        this.params &&
        this.params.start_date !== '' &&
        this.params.useStartDateTime
      ) {
        this.params.start_date =
          this.params.start_date + ' ' + this.params.start_time
        delete this.params.start_time
      }
      if (
        this.params &&
        this.params.end_date !== '' &&
        this.params.useEndDateTime
      ) {
        this.params.end_date = this.params.end_date =
          this.params.end_date + ' ' + this.params.end_time
        delete this.params.end_time
      }
    },
    convertDateParamsToFilters() {
      Object.keys(this.params).forEach(key => {
        if (key === 'selected_time') {
          this.params[`filter_${this.params.selected_time.code}_value`] =
            convertToUTC(this.params.start_date) +
            '|' +
            convertToUTC(this.params.end_date)
          this.params[`filter_${this.params.selected_time.code}_comparison`] =
            'BETWEEN'
          delete this.params.start_date
          delete this.params.end_date
          delete this.params.selected_time
        }
      })
    },
    getMilliseconds(time) {
      const milliseconds = (h, m) => (h * 60 * 60 + m * 60) * 1000
      const timeParts = time.split(':')
      return milliseconds(timeParts[0], timeParts[1])
    },
    getValue(dictionaryName, value) {
      return this.$store.getters['data/getValueFromDictionary'](
        dictionaryName,
        value
      )
    },
    getFilters() {
      const filter = this.$store.getters['filters/getFilters'](this.modelName)
      this.filters = filter ? filter.value : null
      this.showFilters = false
    },
    setFilters() {
      const filter = this.$store.getters['filters/getFilters'](this.modelName)
      this.filters = filter ? filter.value : null
      this.showFilters = false
      this.page = 1
      this.fetchData()
    },
    getPaginator() {
      const pagination = this.$store.getters['pagination/get'](this.modelName)
      if (pagination) {
        this.page = pagination.page || 1
        this.sortBy = pagination.sortBy || this.sortBy
        this.descending = pagination.descending || false
        this.pagination.page = 1
        this.pagination.rowsPerPage = pagination.rowsPerPage || 10
      } else {
        this.page = 1
        this.pagination.page = 1
        this.pagination.rowsPerPage = 10
      }
    },
    changePage(value) {
      this.page = value
      this.fetchData()
    },
    changeRowsPerPage(value) {
      if (this.pagination.rowsPerPage === value) return
      this.pagination = {
        ...this.pagination,
        rowsPerPage: value,
      }
      this.page = 1
      this.$store.dispatch('pagination/saveRowsPerPage', value)
      this.fetchData()
    },
    changeSort(header) {
      if (!header.sortable) return
      if (this.sortBy === header.value) {
        this.descending = !this.descending
      } else {
        this.sortBy = header.value
        this.descending = false
      }
      this.fetchData()
    },
    getSortClass(header) {
      return [
        header.sortable ? 'column sortable' : null,
        this.pagination.descending ? 'desc' : 'asc',
        header.value === this.pagination.sortBy ? 'active' : '',
      ]
    },
    justifyPadding(header) {
      if (header.show === false) {
        return { display: 'none' }
      }
      return {
        'padding-left': '8px',
        'padding-right': '8px',
        'text-align': 'left',
      }
    },
    dateFormat(date) {
      const formatedDate = formatDate(date)
      return formatedDate === 'Invalid Date' ? '-' : formatedDate
    },
    refresh() {
      this.$refs.paramsRef.confirm(true)
    },
    hideValues(field) {
      return hideValues(field, this.headers)
    },
    updateColumns(columns) {
      updateColumns(columns, this.headers)
    },
    openDialog() {
      this.showDialog = true
    },
  },
}
</script>

<template>
  <Layout>
    <div slot="actionButton">
      <v-btn
        color="info"
        @click="refresh"
      >
        <v-icon>refresh</v-icon>
      </v-btn>
      <v-btn
        :color="filters ? `success` : `info`"
        @click="showFilters = true"
      >
        <v-icon>filter_list</v-icon>
      </v-btn>
    </div>
    <parameters
      ref="paramsRef"
      @set-params="setParams($event)"
    />
    <v-layout justify-end>
      <v-btn
        icon
        flat
        @click="openDialog()">
        <v-icon>view_column</v-icon>
      </v-btn>
    </v-layout>
    <v-data-table
      :headers="headers"
      :items="content"
      :pagination="pagination"
      :total-items="totalElements"
      :loading="isLoading"
      :class="$style.sdTable"
      hide-actions
    >
      <template
        slot="headers"
        slot-scope="props">
        <tr>
          <th
            v-for="header in getHeaders"
            :key="header.text"
            :class="getSortClass(header)"
            :style="justifyPadding(header)"
            @click="changeSort(header)"
          >
            <span> {{ $t(`label__${header.text}`) }} </span>
            <v-icon
              v-if="header.sortable"
              small>expand_less</v-icon>
          </th>
        </tr>
      </template>
      <v-progress-linear
        slot="progress"
        height="2"
        color="blue"
        background-color="#E0E0E0"
        indeterminate
      />
      <template
        slot="items"
        slot-scope="props"
      >
        <tr>
          <td class="text-center">
            <router-link
              v-if="props.item.transmitterId && userAllowedToNavigate"
              :to="{ name: 'searchPlug', params: { serial: props.item.transmitterId , goodsGroup: 3 }}"> {{ props.item.transmitterId }}
            </router-link>
            <span v-if="props.item.transmitterId && !userAllowedToNavigate">{{ props.item.transmitterId }}</span>
          </td>
          <td
            v-if="!hideValues('firmwareVersion')"
            :class="$style.oneRowPadding">{{ props.item.firmwareVersion }}</td>
          <td
            v-if="!hideValues('ip1')"
            :class="$style.oneRowPadding">{{ props.item.ip1 }}</td>
          <td
            v-if="!hideValues('ip2')"
            :class="$style.oneRowPadding">{{ props.item.ip2 }}</td>
          <td
            :class="$style.oneRowPadding">{{ dateFormat(props.item.receiveTime) }}</td>
          <td
            v-if="!hideValues('timeSinceLastStart')"
            :class="$style.oneRowPadding">{{ dateFormat(props.item.timeSinceLastStart) }}</td>
          <td
            v-if="!hideValues('connectedPlugs')"
            :class="$style.oneRowPadding">
            <router-link :to="`/heartbeats/connectedPlugs/${props.item.id}`">
              {{ props.item.connectedPlugs }}
            </router-link>
          </td>
          <td
            v-if="!transmitterId && !hideValues('history')"
            :class="$style.oneRowPadding">
            <router-link :to="`/heartbeats/${props.item.transmitterId}`"> {{ $t(`label__history`) }}
            </router-link>
          </td>
        </tr>
      </template>
    </v-data-table>
    <table-paginator
      :page="page"
      :total-pages="totalPages"
      :first="first"
      :last="last"
      :rows-per-page="pagination.rowsPerPage"
      @change-page="changePage($event)"
      @change-rows-per-page="changeRowsPerPage($event)" />
    <adjust-columns-dialog
      v-model="showDialog"
      :headers="getHeaders.filter(h => h.show !== undefined)"
      :model-name="modelName"
      @columns-updated="updateColumns"
    />
    <filters
      :visible="showFilters"
      :model="model"
      :model-name="modelName"
      @close-modal="showFilters = false"
      @filters-confirmed="setFilters()"
    />
  </Layout>
</template>

<style lang="scss" module>
@import '@design';
.sdTable {
  white-space: nowrap;
  border: 1px solid #dfdfdf;

  th {
    outline: none;
  }
}
.sdEditRow {
  cursor: pointer;
}
.oneRowPadding {
  padding-right: 8px !important;
  padding-left: 8px !important;
}

.sdBorder {
  border: 1px solid #dfdfdf;
  border-bottom: none;
}
</style>
